import React from 'react';
import { cn } from '../../lib/utils';
import { Text } from '../Text/Text';

type CallToActionProps = {
  variant: 'callToAction';
  size?: 'normal' | 'small';
};

type BodyProps =
  | {
      variant?: 'body';
      size?: 'large';
      weight?: 'regular' | 'bold' | 'medium';
    }
  | {
      variant?: 'body';
      size?: 'medium';
      weight?: 'regular' | 'medium';
    }
  | {
      variant?: 'body';
      size?: 'small';
      weight?: 'regular' | 'medium' | 'bold';
    };

type TypographyStyleProps =
  | CallToActionProps
  | BodyProps
  | { variant: undefined; weight: undefined };

export type TypographyProps = TypographyStyleProps & {
  className?: string;
  children: React.ReactNode;
};

// Styles based on https://www.figma.com/design/D0EpstolZg8PSBL4rAd1YA/Altify--%3E-Style-Guide?node-id=140-407
const callToActionClassNames = {
  normal: 'font-semibold text-xl',
  small: 'font-semibold text-sm',
};

const bodyClassNames = {
  large: {
    regular: 'font-normal text-lg',
    medium: 'font-semibold text-lg',
    bold: 'font-bold text-lg',
  },
  medium: {
    regular: 'font-normal text-base',
    medium: 'font-semibold text-base',
    // This variant is not in the style guide but it feels weird leaving it out
    bold: 'font-bold text-base',
  },
  small: {
    regular: 'font-normal text-sm',
    medium: 'font-semibold text-sm',
    bold: 'font-bold text-sm',
  },
} as const;

export function getClassNames(props: TypographyStyleProps) {
  if (props.variant === 'callToAction') {
    return callToActionClassNames[props.size ?? 'normal'];
    // We will have 'body' and 'regular' as default
  } else if (!props.variant || props.variant === 'body') {
    // TODO: find out why typescript gives errors here
    // @ts-ignore
    return bodyClassNames[props.size ?? 'medium'][props.weight ?? 'regular'];
  }

  return null;
}

export function Typography(props: TypographyProps) {
  const variantClassNames = getClassNames(props);

  return (
    <Text className={cn(variantClassNames, props.className)}>
      {props.children}
    </Text>
  );
}
